// The league's waiver order and budgets, for everybody, and the commissioner's
// change to one team's place or budget (COMMISSIONER_OVERRIDES.md "The waiver
// order, a budget, and a trade", J7). Two sheets: the order, which every
// member reads; and the change, which only the commissioner of a real league
// opens, saying what will happen before it is saved, with a reason, and the
// server's sentence when it refuses.

import { useEffect, useState } from 'react';
import type { WaiverOrderRow, WaiversView } from '@fsp/domain';
import { TeamMark } from '../../kit';
import { Sheet } from '../../kit/overlays';
import {
  tell,
  Button,
  Callout,
  Caption,
  Choice,
  ListRow,
  NumberField,
  OneOf,
  RowName,
  RowSub,
  Stack,
  Text,
  TextArea,
} from '../../kit/ui';
import { messageOf } from '../../data/api';
import { setBudget, setWaiverPriority } from '../../data/moves';
import { TeamName } from './Names';
import { ordinal } from './RosterMoveSheet';

/** Where the order comes from, in a line under the sheet's title. */
function sourceLine(view: WaiversView): string | null {
  switch (view.orderSource) {
    case 'rolling':
      return 'Every claim won moves that team to the back.';
    case 'standings':
      return 'Worst record first, worked out again for every run.';
    case 'draft-order':
      return 'The reverse of the draft, until a game is final.';
    case 'frozen':
      return 'The regular season’s final table, through the playoffs.';
    case null:
      return view.system === 'faab' ? 'Tied bids go to the earlier claim.' : null;
  }
}

/** What the order's list is called: the order, or in a league with none, the budgets. */
export function orderTitle(view: WaiversView): string {
  return view.orderSource === null ? 'Budgets' : 'Waiver order';
}

export function WaiverOrderSheet({
  view,
  opened,
  onClose,
  onChange,
}: {
  view: WaiversView;
  opened: boolean;
  onClose: () => void;
  /** The commissioner's way to change a team; null for everybody else. */
  onChange: ((row: WaiverOrderRow) => void) | null;
}) {
  const line = sourceLine(view);
  return (
    <Sheet opened={opened} onClose={onClose} title={orderTitle(view)}>
      <Stack gap={2}>
        {line !== null ? <Caption>{line}</Caption> : null}
        {view.commissioner?.orderNote ? <Callout>{view.commissioner.orderNote}</Callout> : null}
        <WaiverOrderRows view={view} onChange={onChange} />
      </Stack>
    </Sheet>
  );
}

/**
 * Every team's place and budget left, mine marked: the sheet's list, and on a
 * tablet or a desktop the Waivers page's own under my claims (B-391).
 */
export function WaiverOrderRows({
  view,
  onChange,
  withSource = false,
}: {
  view: WaiversView;
  /** The commissioner's way to change a team; null for everybody else. */
  onChange: ((row: WaiverOrderRow) => void) | null;
  /** The line saying where the order comes from, above the rows. */
  withSource?: boolean;
}) {
  const line = withSource ? sourceLine(view) : null;
  const changeable =
    onChange !== null &&
    view.commissioner !== null &&
    (view.commissioner.order || view.commissioner.budgets);
  return (
    <div>
      {line !== null ? (
        <div className="fsp-waivers-order-source">
          <Text tone="secondary" size="xs" block>
            {line}
          </Text>
        </div>
      ) : null}
      {view.order.map((row) => (
        <ListRow
          key={row.team.id}
          selected={row.team.isMine}
          lead={<TeamMark team={row.team} size={28} />}
          trail={
            <>
              {row.budgetLeft !== null ? <span className="fsp-num">${row.budgetLeft}</span> : null}
              {changeable ? (
                <Button size="xs" variant="quiet" onClick={() => onChange(row)}>
                  Change
                </Button>
              ) : null}
            </>
          }
        >
          <RowName>
            {row.rank !== null ? `${ordinal(row.rank)} · ` : ''}
            <TeamName team={row.team} />
          </RowName>
          <RowSub>{row.team.ownerName}</RowSub>
        </ListRow>
      ))}
    </div>
  );
}

type What = 'priority' | 'budget';

/** What a move to `to` does, before it is saved. */
export function priorityConsequences(
  order: readonly WaiverOrderRow[],
  row: WaiverOrderRow,
  to: number,
): string[] {
  const from = row.rank ?? to;
  const passed = order.filter(
    (r) =>
      r.rank !== null &&
      r.team.id !== row.team.id &&
      (to < from ? r.rank >= to && r.rank < from : r.rank > from && r.rank <= to),
  );
  const out = [`${row.team.name} goes from ${ordinal(from)} to ${ordinal(to)}.`];
  if (passed.length === 1)
    out.push(`${passed[0]!.team.name} moves ${to < from ? 'down' : 'up'} one place.`);
  else if (passed.length > 1)
    out.push(
      `The teams from ${ordinal(Math.min(...passed.map((r) => r.rank!)))} to ${ordinal(
        Math.max(...passed.map((r) => r.rank!)),
      )} each move ${to < from ? 'down' : 'up'} one place.`,
    );
  out.push('From here, each claim won still sends its team to the back.');
  // Nobody is told of their own change, and a bot's seat has nobody to tell.
  out.push(
    'The managers whose place changes are told, other than you and a bot’s seat, and the league chat and the commissioner log say so, with your reason.',
  );
  return out;
}

/** What setting a budget does, before it is saved. */
export function budgetConsequences(row: WaiverOrderRow, left: number): string[] {
  return [
    `The budget of ${row.team.name} goes from $${row.budgetLeft ?? 0} to $${left}.`,
    `A pending bid above $${left} stays in, and loses at the run as over budget.`,
    row.team.isMine
      ? 'The league chat and the commissioner log say so, with your reason.'
      : 'Its manager is told, unless a bot holds the seat, and the league chat and the commissioner log say so, with your reason.',
  ];
}

export function WaiverChangeSheet({
  view,
  row,
  leagueId,
  onClose,
}: {
  view: WaiversView;
  row: WaiverOrderRow | null;
  leagueId: string;
  onClose: () => void;
}) {
  const can = view.commissioner;
  const canOrder = can?.order === true && row?.rank !== null;
  const canBudget = can?.budgets === true && row?.budgetLeft !== null;
  const [what, setWhat] = useState<What>('priority');
  const [rank, setRank] = useState<number | null>(null);
  const [left, setLeft] = useState<number | null>(null);
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  // A fresh start for each team the sheet opens on.
  useEffect(() => {
    setWhat(canOrder ? 'priority' : 'budget');
    setRank(row?.rank ?? null);
    setLeft(row?.budgetLeft ?? null);
    setReason('');
    setProblem(null);
  }, [row?.team.id]);

  const close = () => {
    setProblem(null);
    onClose();
  };

  const mode: What = canOrder && canBudget ? what : canOrder ? 'priority' : 'budget';
  const changed =
    row !== null &&
    (mode === 'priority'
      ? rank !== null && rank !== row.rank
      : left !== null && left !== row.budgetLeft);

  const save = async () => {
    if (row === null || !changed) return;
    setSaving(true);
    setProblem(null);
    const reply =
      mode === 'priority'
        ? await setWaiverPriority(leagueId, row.team.id, rank!, reason.trim())
        : await setBudget(leagueId, row.team.id, left!, reason.trim());
    setSaving(false);
    if (reply.status !== 'ok')
      return setProblem(
        messageOf(
          reply,
          'That did not go through.',
          'Could not reach the site, so we cannot tell whether that was saved. Try again: if it was, you will be told it already is.',
        ),
      );
    close();
    tell(mode === 'priority' ? 'Waiver order changed.' : 'Budget changed.');
  };

  const places = view.order.filter((r) => r.rank !== null).length;
  return (
    <Sheet
      opened={row !== null}
      onClose={close}
      title={row ? `Change ${row.team.name}` : undefined}
    >
      {row !== null && (canOrder || canBudget) ? (
        <Stack gap={3}>
          {canOrder && canBudget ? (
            <OneOf
              label="What to change"
              options={[
                { value: 'priority', label: 'Place in the order' },
                { value: 'budget', label: 'Budget' },
              ]}
              value={what}
              onChange={setWhat}
            />
          ) : null}
          {mode === 'priority' ? (
            <Choice
              label="New place"
              searchable={false}
              required
              options={Array.from({ length: places }, (_, i) => ({
                value: String(i + 1),
                label: ordinal(i + 1),
              }))}
              value={rank === null ? null : String(rank)}
              onChange={(v) => setRank(v === null ? null : Number(v))}
            />
          ) : (
            <NumberField
              label="New budget"
              description={`$0 to the league’s $${can?.budgetMax ?? 0}`}
              prefix="$"
              min={0}
              max={can?.budgetMax ?? undefined}
              value={left}
              onChange={setLeft}
            />
          )}
          {changed ? (
            <Callout>
              <Stack gap={1}>
                {(mode === 'priority'
                  ? priorityConsequences(view.order, row, rank!)
                  : budgetConsequences(row, left!)
                ).map((line) => (
                  <Text key={line} block>
                    {line}
                  </Text>
                ))}
              </Stack>
            </Callout>
          ) : null}
          <TextArea
            label="Why"
            placeholder="The reason, in a sentence the league will read"
            value={reason}
            onChange={setReason}
            maxLength={300}
          />
          {problem !== null ? <Callout tone="bad">{problem}</Callout> : null}
          <Button
            onClick={() => void save()}
            disabled={!changed || reason.trim() === '' || saving}
            loading={saving}
          >
            Save
          </Button>
        </Stack>
      ) : null}
    </Sheet>
  );
}
